import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import RibbonNavbar from "../../components/RibbonNavbar";
import SearchInput from "../../components/SearchInput";
import SearchableDataGrid from '../../components/SearchableDataGrid';
import { iepaProd } from "../../constants/global";

const RulesList = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const navigate = useNavigate();

    const [rules, setRules] = useState([]);
    const [filterText, setFilterText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);

    // ================= FETCH RULES =================
    const fetchRules = async () => {
    try {
        const res = await fetch(
            `${iepaProd.iepaAdapterSrvAPI}/adapters/rules/active/org/1`
        );

        const data = await res.json();

        if (Array.isArray(data)) {
            const formatted = data.map((rule, index) => ({
                ...rule,
                id: rule.id,
                sno: index + 1,
                query: rule.adapterRuleConditions
                    ?.map(
                        (c) =>
                            `${c.attributeValue} ${c.attributeOperator}`
                    )
                    .join(" ")
            }));

            setRules(formatted);
        } else {
            setRules([]);
        }
    } catch (err) {
        console.error("Error fetching rules:", err);
        setRules([]);
    }
};
useEffect(() => {
    fetchRules();
}, []);

    // ================= FILTER =================
    const filteredRows = rules.filter((rule) => {
        const search = filterText.toLowerCase();
        return (
            rule.name?.toLowerCase().includes(search) ||
            rule.status?.toLowerCase().includes(search) ||
            rule.query?.toLowerCase().includes(search)
        );
    });

    // ================= COLUMNS =================
    const columns = [
        { field: "sno", headerName: "S No", width: 80 },
        {
            field: "name",
            headerName: "Name",
            flex: 1,
        },
        {
            field: "startDate",
            headerName: "Start Date",
            flex: 1,
        },
        {
            field: "endDate",
            headerName: "End Date",
            flex: 1,
        },
        {
            field: "status",
            headerName: "Status",
            flex: 1,
        },
        {
            field: "query",
            headerName: "Query",
            flex: 2,
        },
    ];

    const handleDeactivate = async () => {
    if (selectedRows.length === 0) return;

    const confirmDelete = window.confirm(
        "Are you sure you want to deactivate selected rule(s)?"
    );

    if (!confirmDelete) return;

    try {
        // Supports multiple selection automatically
        await Promise.all(
            selectedRows.map((ruleId) =>
                fetch(
                    `${iepaProd.iepaAdapterSrvAPI}/adapters/rules/${ruleId}/deactivate/org/1`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json"
                        }
                    }
                )
            )
        );

        alert("Rule(s) deactivated successfully");

        // ✅ Re-fetch fresh data from server
        await fetchRules();

        // ✅ Clear selection
        setSelectedRows([]);

    } catch (error) {
        console.error("Deactivate failed:", error);
        alert("Failed to deactivate rule(s)");
    }
};

    return (
        <Box m="20px">
            {/* HEADER + SEARCH + RIBBON */}
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Header title="Source Mapping Rule" />


                <RibbonNavbar
                selectedRows={selectedRows} 
                    showAdd
                    showEdit
                    showView
                    showDelete
                    onAdd={() => navigate("../Rules")}
                    onEdit={() => {
                        const selectedRule = rules.find(
                            (rule) => rule.id === selectedRows[0]
                        );

                        navigate("/Rules", {
                            state: {
                                ruleData: selectedRule,
                                isEditMode: true,
                            },
                        });
                    }}
                    onView={() => {
                        const selectedRule = rules.find(
                            (rule) => rule.id === selectedRows[0]
                        );

                        navigate("/Rules", {
                            state: {
                                ruleData: selectedRule,
                                isEditMode: false,
                            },
                        });
                    }}
                    onDelete={handleDeactivate}
                    disabledEdit={selectedRows.length === 0}
                    disabledView={selectedRows.length === 0}
                    disabledDelete={selectedRows.length === 0}
                />
            </Box>

            {/* GRID */}
            <Box
                height="78vh"
                sx={{
                    "& .MuiDataGrid-root": {
                        border: "none",
                    },
                    "& .MuiDataGrid-cell": {
                        borderBottom: "1px solid grey",
                        whiteSpace: "normal",
                        wordWrap: "break-word",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: colors.blueAccent[700],
                        fontSize: "14px",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                        backgroundColor: colors.primary[400],
                    },
                    "& .MuiDataGrid-footerContainer": {
                        backgroundColor: colors.blueAccent[900],
                    },
                }}
            >
                <SearchableDataGrid
                    rows={filteredRows}
                    columns={columns}
                    checkboxSelection
                    rowSelectionModel={selectedRows}
                    onRowSelectionModelChange={(newSelection) =>
                        setSelectedRows(newSelection)
                    }
                    components={{ Toolbar: GridToolbar }}
                />
            </Box>
        </Box>
    );
};

export default RulesList;
